// components/StyledCurrencyInput.tsx
import {
  Checkbox,
  FormControlLabel,
  Radio,
  styled,
  Switch,
  TextField,
  TextFieldProps,
} from '@mui/material';
import React from 'react';
import { NumericFormat } from 'react-number-format';
import { Palette } from 'Palette';

const INTER_FONT_FAMILY = 'Inter, sans-serif';

type StyledInputProps = TextFieldProps & {
  noDisableGrey?: boolean;
};

const RawStyledInput = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'noDisableGrey',
})<StyledInputProps>(({ className, noDisableGrey }) => ({
  marginTop: '0.5rem',
  borderRadius: '0.5rem',
  fontFamily: INTER_FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 400,
  color: Palette['default'].text,
  className: `react-StyledInput ${className || ''}`.trim(),
  '& .MuiInputBase-root': {
    fontFamily: INTER_FONT_FAMILY,
    fontSize: '1rem',
    fontWeight: 400,
    color: Palette['default'].text,
  },
  '& .MuiInputBase-input': {
    fontFamily: INTER_FONT_FAMILY,
    fontSize: '1rem',
    fontWeight: 400,
    color: Palette['default'].text,
  },
  '& .MuiInputLabel-root': {
    fontFamily: INTER_FONT_FAMILY,
    fontSize: '1rem',
    fontWeight: 400,
    color: Palette['default'].text,
  },
  '& .MuiFormHelperText-root': {
    fontFamily: INTER_FONT_FAMILY,
    fontSize: '0.875rem',
    fontWeight: 400,
    color: Palette['default'].text,
  },
  ...(noDisableGrey && {
    '& .MuiInputBase-root.Mui-disabled': {
      backgroundColor: 'transparent',
      color: Palette['default'].text,
      '& .MuiInputBase-input.Mui-disabled': {
        '-webkit-text-fill-color': Palette.default.text,
      },
      opacity: 1,
    },
  }),
}));

export const StyledInput = React.forwardRef<any, StyledInputProps>(
  function ForwardedStyledInput(props, ref) {
    return <RawStyledInput {...props} inputRef={ref} />;
  },
);

export const StyledCheckbox = styled(Checkbox)(() => ({
  color: Palette['tone1'].buttonText,
  '&.Mui-checked': {
    color: Palette['tone1'].buttonText, // color when checked
  },
}));

export const StyledRadio = styled(Radio)(() => ({
  color: Palette['default'].text,
  '&.Mui-checked': {
    color: Palette['default'].buttonBg, // color when checked
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

export const StyledSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: Palette['default'].buttonText,
    '& + .MuiSwitch-track': {
      backgroundColor: Palette['default'].buttonBg,
    },
  },
  '& .MuiSwitch-switchBase': {
    '& + .MuiSwitch-track': {
      backgroundColor: Palette['default'].border,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: Palette['default'].border,
  },
}));

interface StyledCurrencyInputProps {
  value?: number | string;
  onValueChange: (value: number | undefined) => void;
  prefix?: string;
  allowDecimals?: boolean;
  allowNegativeValue?: boolean;
  placeholder?: string;
  className?: string;
  name?: string;
  disabled?: boolean;
  variant?: TextFieldProps['variant'];
  error?: boolean;
  helperText?: string;
  sx?: object;
  noDisabledGrey?: boolean;
}

export const NoDisabledGreyStyledInput: React.FC<StyledInputProps> = (props) => {
  return <StyledInput {...(props as StyledInputProps)} noDisableGrey={true} />;
};

export const StyledCurrencyInput: React.FC<StyledCurrencyInputProps> = ({
  value,
  onValueChange,
  prefix = '$',
  allowDecimals = false,
  allowNegativeValue = false,
  placeholder,
  className,
  name,
  disabled,
  variant = 'outlined',
  error,
  helperText,
  sx,
  noDisabledGrey = false,
}) => {
  return (
    <NumericFormat
      value={value}
      customInput={
        (noDisabledGrey ? NoDisabledGreyStyledInput : StyledInput) as React.ComponentType<
          TextFieldProps & { noDisableGrey?: boolean }
        >
      }
      thousandSeparator
      decimalScale={allowDecimals ? 2 : 0}
      fixedDecimalScale={allowDecimals}
      allowNegative={allowNegativeValue}
      prefix={prefix}
      placeholder={placeholder}
      className={`react-StyledCurrencyInput ${className || ''}`.trim()}
      name={name}
      disabled={disabled}
      variant={variant}
      error={error}
      helperText={helperText}
      onValueChange={({ floatValue }) => onValueChange(floatValue ?? undefined)}
      sx={{
        ...sx,
        fontFamily: INTER_FONT_FAMILY,
        fontSize: '1rem',
        fontWeight: 400,
        color: Palette['default'].text,
        '& .MuiInputBase-root': {
          fontFamily: INTER_FONT_FAMILY,
          fontSize: '1rem',
          fontWeight: 400,
          color: Palette['default'].text,
        },
        '& .MuiInputBase-input': {
          fontFamily: INTER_FONT_FAMILY,
          fontSize: '1rem',
          fontWeight: 400,
          color: Palette['default'].text,
        },
        '& .MuiInputLabel-root': {
          fontFamily: INTER_FONT_FAMILY,
          fontSize: '1rem',
          fontWeight: 400,
          color: Palette['default'].text,
        },
        '& .MuiFormHelperText-root': {
          fontFamily: INTER_FONT_FAMILY,
          fontSize: '0.875rem',
          fontWeight: 400,
          color: Palette['default'].text,
        },
        ...(noDisabledGrey && {
          '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: 'transparent',
            color: Palette.default.text,
            '-webkit-text-fill-color': Palette.default.text, // for Safari
            opacity: 1,
          },
        }),
      }}
    />
  );
};
type StyledFormControlLabelProps = React.ComponentProps<typeof FormControlLabel> & {
  tone?: keyof typeof Palette;
};

export const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<StyledFormControlLabelProps>(({ tone = 'default' }) => ({
  '& .MuiFormControlLabel-label': {
    fontFamily: INTER_FONT_FAMILY,
    fontSize: '1rem',
    fontWeight: 400,
    color: Palette[tone]?.text ?? Palette['default'].text,
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
  },
}));
